import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useProject } from '../../Layout/header/context';
import { useStyles } from '../styles/moreDetailsStyles';
import { PROJECT_STATUS_LIST, STAKEHOLDER_OVERVIEW_VISIBILITY } from '../../../../Constants/ProjectConstants';
import { getAllClients } from '../../../../services/clientService';
import { updateProject } from '../../../../services/projectService';
import { isStakeholder } from '../../../../utils/share';

export const STATUS_COLORS = {
  Completed: {
    bg: '#e8f5e9ff',
    text: '#2E7D32',
  },
  'Under Support': {
    bg: '#e8f5e9ff',
    text: '#2E7D32',
  },
  'On Track': {
    bg: '#E8F5E9',
    text: '#2E7D32',
  },
  'At Risk': {
    bg: '#FDECEA',
    text: '#ce0000ff',
  },
  Delayed: {
    bg: '#FDECEA',
    text: '#ce0000ff',
  },
  'On Hold': {
    bg: '#D0F2FF',
    text: '#04297A',
  },
  'Not Started': {
    bg: '#ECEFF1',
    text: '#455A64',
  },
  'In UAT': {
    bg: '#E8F5E9',
    text: '#2E7D32',
  },
};

export const RISK_COLORS = {
  'No Risk': {
    bg: '#D0F2FF',
    text: '#04297A',
  },
  Low: {
    bg: '#FFF6CC',
    text: '#694800ff',
  },
  Medium: {
    bg: '#FFF8E1',
    text: '#e88f00ff',
  },
  High: {
    bg: '#FDECEA',
    text: '#cf0e0eff',
  },
};

const label = { inputProps: { 'aria-label': 'Checkbox demo' } };
const PROJECT_HEALTH_LIST = [
  'Completed',
  'Not Started',
  'On Track',
  'On Hold',
  'Delayed',
  'At Risk',
  'In UAT',
  'Under Support',
];

const PROJECT_RISK_LIST = ['Low', 'Medium', 'High', 'No Risk'];


const MoreDetails = () => {
  const {
    projectDetails,
    setProjectDetails,
    isEditPermission,
    userIsClient,
    userIsStakeholder,
    updateOverviewVisibility,
    canConfigureStakeholderVisibility,
  } = useProject();
  const classes = useStyles();
  const [clientList, setClientList] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [enteredTotalHrs, setEnteredTotalHrs] = useState(projectDetails?.totalHrs);
  const [enteredInitialEstimatedHrs, setEnteredInitialEstimatedHrs] = useState(projectDetails?.initialEstimatedHrs);

  const isFieldVisible = (fieldKey) => !userIsStakeholder || STAKEHOLDER_OVERVIEW_VISIBILITY[fieldKey] !== false;

  // A regular project member (contributor role, not admin/manager/owner/team-leader,
  // and not a client or stakeholder) must not see the project's estimated hours.
  const userIsProjectMember = !isEditPermission && !userIsStakeholder && !userIsClient;

  const handleVisibilityToggle = (fieldKey) => async (event) => {
    try {
      setDisabled(`visibility:${fieldKey}`);
      await updateOverviewVisibility({ [fieldKey]: event.target.checked });
    } catch (error) {
      const errMsg =
        error?.response?.data?.message ||
        'An error occurred while updating stakeholder visibility. Please try again later.';
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const renderFieldHeader = (title, fieldKey) => (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
      <Typography variant="h6">{title}</Typography>
      {canConfigureStakeholderVisibility && (
        <FormControlLabel
          sx={{ mr: 0, ml: 0, alignItems: 'center' }}
          labelPlacement="start"
          control={
            <Switch
              size="small"
              checked={STAKEHOLDER_OVERVIEW_VISIBILITY[fieldKey] !== false}
              onChange={handleVisibilityToggle(fieldKey)}
              disabled={disabled === `visibility:${fieldKey}`}
            />
          }
          label={<Typography variant="caption">Stakeholder</Typography>}
        />
      )}
    </Stack>
  );

  const renderField = (fieldKey, title, content, gridProps = { xs: 12, sm: 3 }) => {
    if (!isFieldVisible(fieldKey)) return null;

    return (
      <Grid item {...gridProps}>
        {renderFieldHeader(title, fieldKey)}
        {content}
      </Grid>
    );
  };

  const handleClientChange = async (e, value) => {
    if (!isEditPermission || value?.id === projectDetails?.clientId) return;
    try {
      setSelectedClient(value);
      setDisabled('clientId');
      await updateProject(projectDetails?.id, { clientId: value?.id });
      setProjectDetails((prev) => ({ ...prev, clientId: value?.id }));
      setSelectedClient(value);
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleStatusChange = async (e) => {
    const status = e.target.value;
    if (!isEditPermission || status === projectDetails?.status) return;
    try {
      setDisabled('status');
      await updateProject(projectDetails?.id, { status });
      setProjectDetails((prev) => ({ ...prev, status }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleHealthChange = async (e) => {
    const health = e.target.value;
    if (!isEditPermission || health === projectDetails?.health) return;
    try {
      setDisabled('health');
      await updateProject(projectDetails?.id, { health });
      setProjectDetails((prev) => ({ ...prev, health }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleRiskChange = async (e) => {
    const risk = e.target.value;
    if (!isEditPermission || risk === projectDetails?.risk) return;
    try {
      setDisabled('risk');
      await updateProject(projectDetails?.id, { risk });
      setProjectDetails((prev) => ({ ...prev, risk }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const hanldeTotalHrsChange = (e) => {
    const inputValue = e.target.value;
    const isValidInput = /^-?\d*\.?\d*$/.test(inputValue);

    if (isValidInput && inputValue >= 0) {
      setEnteredTotalHrs(inputValue);
    }
  };

  const handleTotalHrsBlur = async () => {
    const totalHrs = parseFloat(enteredTotalHrs);
    if (!isEditPermission || totalHrs === projectDetails?.totalHrs) return;
    try {
      setDisabled('totalHrs');
      await updateProject(projectDetails?.id, { totalHrs });
      setProjectDetails((prev) => ({ ...prev, totalHrs }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleInitialEstimatedHrsChange = (e) => {
    const inputValue = e.target.value;
    const isValidInput = /^-?\d*\.?\d*$/.test(inputValue);

    if (isValidInput && inputValue >= 0) {
      setEnteredInitialEstimatedHrs(inputValue);
    }
  };

  const handleInitialEstimatedHrsBlur = async () => {
    const initialEstimatedHrs = parseFloat(enteredInitialEstimatedHrs);
    if (!isEditPermission || initialEstimatedHrs === projectDetails?.initialEstimatedHrs) return;
    try {
      setDisabled('initialEstimatedHrs');
      await updateProject(projectDetails?.id, { initialEstimatedHrs });
      setProjectDetails((prev) => ({ ...prev, initialEstimatedHrs }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleCheckChange = (name) => async (event) => {
    try {
      if (!isEditPermission) return;
      const check = event.target.checked;
      setDisabled(name);
      await updateProject(projectDetails?.id, { [name]: check });
      setProjectDetails((prev) => ({ ...prev, [name]: check }));
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  const handleDateChange = (name) => async (value) => {
    try {
      if (!isEditPermission) return;
      setDisabled(name);
      const formattedValue = value ? dayjs(value).format('YYYY-MM-DD') : null;

      const uatDate = projectDetails?.projectUATDate ? dayjs(projectDetails.projectUATDate, 'YYYY-MM-DD') : null;
      const liveDate = projectDetails?.ProjectLiveDate ? dayjs(projectDetails.ProjectLiveDate, 'YYYY-MM-DD') : null;

      if (name === 'projectLiveDate' && value && uatDate && dayjs(value).isBefore(uatDate)) {
        toast.error('Live Date cannot be before UAT Date.');
        setDisabled(false);
        return;
      }

      if (name === 'projectUATDate' && value && liveDate && dayjs(value).isAfter(liveDate)) {
        toast.error('UAT Date cannot be after Live Date.');
        setDisabled(false);
        return;
      }

      const startDate = projectDetails?.startDate ? dayjs(projectDetails.startDate, 'YYYY-MM-DD') : null;
      const endDate = projectDetails?.endDate ? dayjs(projectDetails.endDate, 'YYYY-MM-DD') : null;
      const actualStartDate = projectDetails?.actualStartDate
        ? dayjs(projectDetails.actualStartDate, 'YYYY-MM-DD')
        : null;
      const actualEndDate = projectDetails?.actualEndDate ? dayjs(projectDetails.actualEndDate, 'YYYY-MM-DD') : null;

      if (name === 'endDate' && value && startDate && dayjs(value).isBefore(startDate)) {
        toast.error('End Date cannot be before Start Date.');
        setDisabled(false);
        return;
      }
      if (name === 'actualEndDate' && value && actualStartDate && dayjs(value).isBefore(actualStartDate)) {
        toast.error('Actual End Date cannot be before Actual Start Date.');
        setDisabled(false);
        return;
      }
      if (name === 'startDate' && value && endDate && dayjs(value).isAfter(endDate)) {
        toast.error('Start Date cannot be after End Date.');
        setDisabled(false);
        return;
      }
      if (name === 'actualStartDate' && value && actualEndDate && dayjs(value).isAfter(actualEndDate)) {
        toast.error('Actual Start Date cannot be after Actual End Date.');
        setDisabled(false);
        return;
      }

      if (name === 'projectLiveDate') {
        await updateProject(projectDetails?.id, { ProjectLiveDate: formattedValue });
        setProjectDetails((prev) => ({ ...prev, ProjectLiveDate: formattedValue }));
      } else {
        await updateProject(projectDetails?.id, { [name]: formattedValue });
        setProjectDetails((prev) => ({ ...prev, [name]: formattedValue }));
      }
    } catch (error) {
      const errMsg =
        error?.response?.data?.message || 'An error occurred while updating the project. Please try again later.';
      toast.error(errMsg);
    } finally {
      setDisabled(false);
    }
  };

  useEffect(() => setEnteredTotalHrs(projectDetails?.totalHrs), [projectDetails?.totalHrs]);
  useEffect(
    () => setEnteredInitialEstimatedHrs(projectDetails?.initialEstimatedHrs),
    [projectDetails?.initialEstimatedHrs]
  );

  useEffect(() => {
    const fetchClientsData = async () => {
      try {
        const response = await getAllClients();
        const data = response?.data?.data?.rows || [];
        setClientList(data);

        const clientObj = data.find((c) => c.id === projectDetails?.clientId);
        setSelectedClient(clientObj || null);
      } catch (error) {
        const errMsg =
          error?.response?.data?.message || 'An error occurred while fetching the client list. Please try again later.';
        toast.error(errMsg);
      }
    };

    fetchClientsData();
  }, [projectDetails?.clientId, projectDetails?.client]);

  return (
    <Accordion className={classes?.accordion} expanded>
      <AccordionSummary className={classes?.summary}>
        <Typography variant="h4" color="GrayText">
          Project Details
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Grid container spacing={2} columnSpacing={20}>
          {/* Section 1: Basic Project Information */}
          {renderField(
            'clientId',
            'Client',
            <Autocomplete
              size="small"
              disableClearable
              disabled={!isEditPermission || disabled === 'clientId' || userIsClient}
              options={clientList}
              getOptionLabel={(option) => option?.clientName}
              value={selectedClient}
              onChange={handleClientChange}
              autoComplete
              includeInputInList
              renderInput={(params) => <TextField {...params} />}
            />,
            { xs: 12, sm: 6 }
          )}

          {renderField(
            'status',
            'Project Status',
            <Select
              value={projectDetails?.status}
              onChange={handleStatusChange}
              size="small"
              fullWidth
              sx={{ textTransform: 'capitalize' }}
              disabled={!isEditPermission || disabled === 'status'}
              IconComponent={userIsStakeholder ? () => null : undefined}
            >
              {PROJECT_STATUS_LIST.sort().map((list) => (
                <MenuItem sx={{ textTransform: 'capitalize' }} value={list} key={list}>
                  {list}
                </MenuItem>
              ))}
            </Select>,
            { xs: 12, sm: 6 }
          )}

          {!userIsProjectMember &&
            renderField(
              'initialEstimatedHrs',
              'Project Estimated Hours',
              <TextField
                fullWidth
                type="text"
                size="small"
                autoComplete="off"
                inputMode="numeric"
                disabled={!isEditPermission || disabled === 'initialEstimatedHrs'}
                value={enteredInitialEstimatedHrs}
                onChange={handleInitialEstimatedHrsChange}
                onBlur={handleInitialEstimatedHrsBlur}
              />,
              { xs: 12, sm: 6 }
            )}

          {!userIsProjectMember && renderField(
            'totalHrs',
            'Budgeted Hours',
            <TextField
              fullWidth
              type="text"
              size="small"
              autoComplete="off"
              inputMode="numeric"
              disabled={!isEditPermission || disabled === 'totalHrs'}
              value={enteredTotalHrs}
              onChange={hanldeTotalHrsChange}
              onBlur={handleTotalHrsBlur}
            />,
            { xs: 12, sm: 6 }
          )}
          {/* Section 3: Dates & Status */}
          {!userIsProjectMember && renderField(
            'startDate',
            'Planned Start Date',
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="startDate"
                value={projectDetails?.startDate ? dayjs(projectDetails.startDate) : null}
                onChange={(newValue) => handleDateChange('startDate')(newValue)}
                inputFormat="DD MMM YYYY"
                disabled={userIsStakeholder}
                renderInput={(params) => (
                  <TextField
                    size="small"
                    {...params}
                    helperText=""
                    fullWidth
                    InputProps={{ ...params.InputProps, endAdornment: userIsStakeholder ? null : params.InputProps?.endAdornment }}
                  />
                )}
              />
            </LocalizationProvider>,
            { xs: 12, sm: 6 }
          )}

          {!userIsProjectMember &&renderField(
            'endDate',
            'Planned End Date',
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="endDate"
                value={projectDetails?.endDate ? dayjs(projectDetails.endDate) : null}
                onChange={(newValue) => handleDateChange('endDate')(newValue)}
                inputFormat="DD MMM YYYY"
                disabled={userIsStakeholder}
                renderInput={(params) => (
                  <TextField
                    size="small"
                    {...params}
                    helperText=""
                    fullWidth
                    InputProps={{ ...params.InputProps, endAdornment: userIsStakeholder ? null : params.InputProps?.endAdornment }}
                  />
                )}
              />
            </LocalizationProvider>,
            { xs: 12, sm: 6 }
          )}

          {!userIsProjectMember && renderField(
            'actualStartDate',
            'Actual Start Date',
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="actualStartDate"
                value={projectDetails?.actualStartDate ? dayjs(projectDetails.actualStartDate) : null}
                onChange={(newValue) => handleDateChange('actualStartDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>,
            { xs: 12, sm: 6 }
          )}

          {!userIsProjectMember && renderField(
            'actualEndDate',
            'Actual End Date',
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="actualEndDate"
                value={projectDetails?.actualEndDate ? dayjs(projectDetails.actualEndDate) : null}
                onChange={(newValue) => handleDateChange('actualEndDate')(newValue)}
                inputFormat="DD MMM YYYY"
                renderInput={(params) => <TextField size="small" {...params} helperText="" fullWidth />}
              />
            </LocalizationProvider>,
            { xs: 12, sm: 6 }
          )}

          {!userIsProjectMember && renderField(
            'projectUATDate',
            'Project UAT Date',
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="projectUATDate"
                value={projectDetails?.projectUATDate ? dayjs(projectDetails.projectUATDate) : null}
                onChange={(newValue) => handleDateChange('projectUATDate')(newValue)}
                inputFormat="DD MMM YYYY"
                disabled={userIsStakeholder}
                renderInput={(params) => (
                  <TextField
                    size="small"
                    {...params}
                    helperText=""
                    fullWidth
                    InputProps={{ ...params.InputProps, endAdornment: userIsStakeholder ? null : params.InputProps?.endAdornment }}
                  />
                )}
              />
            </LocalizationProvider>,
            { xs: 12, sm: 6 }
          )}

          {!userIsProjectMember && renderField(
            'projectLiveDate',
            'Project Release Date',
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                name="projectLiveDate"
                value={projectDetails?.ProjectLiveDate ? dayjs(projectDetails.ProjectLiveDate) : null}
                onChange={(newValue) => handleDateChange('projectLiveDate')(newValue)}
                inputFormat="DD MMM YYYY"
                disabled={userIsStakeholder}
                renderInput={(params) => (
                  <TextField
                    size="small"
                    {...params}
                    helperText=""
                    fullWidth
                    InputProps={{ ...params.InputProps, endAdornment: userIsStakeholder ? null : params.InputProps?.endAdornment }}
                  />
                )}
              />
            </LocalizationProvider>,
            { xs: 12, sm: 6 }
          )}

          {renderField(
            'health',
            'Current Status',
            <Select
              value={projectDetails?.health}
              onChange={handleHealthChange}
              size="small"
              fullWidth
              sx={{
                textTransform: 'capitalize',
                backgroundColor: STATUS_COLORS[projectDetails?.health]?.bg,
                color: STATUS_COLORS[projectDetails?.health]?.text,
              }}
              disabled={!isEditPermission || disabled === 'health'}
              IconComponent={userIsStakeholder ? () => null : undefined}
            >
              {PROJECT_HEALTH_LIST.map((list) => (
                <MenuItem sx={{ textTransform: 'capitalize' }} value={list} key={list}>
                  {list}
                </MenuItem>
              ))}
            </Select>,
            { xs: 12, sm: 6 }
          )}

          {renderField(
            'risk',
            'Project Risk Status',
            <Select
              value={projectDetails?.risk}
              sx={{
                transform: 'capitalize',
                backgroundColor: RISK_COLORS[projectDetails?.risk]?.bg,
                color: RISK_COLORS[projectDetails?.risk]?.text,
              }}
              onChange={handleRiskChange}
              size="small"
              fullWidth
              disabled={!isEditPermission || disabled === 'risk'}
              IconComponent={userIsStakeholder ? () => null : undefined}
            >
              {PROJECT_RISK_LIST.map((list) => (
                <MenuItem sx={{ textTransform: 'capitalize' }} value={list} key={list}>
                  {list}
                </MenuItem>
              ))}
            </Select>,
            { xs: 12, sm: 6 }
          )}
          {/* Section 2: Allow Excess & Manual Task Entry */}
          {!isStakeholder() && <Grid item xs={12} sm={4}>
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={projectDetails?.allowOverSpent}
                {...label}
                disabled={!isEditPermission || disabled === 'allowOverSpent'}
                onChange={handleCheckChange('allowOverSpent')}
              />
              <Typography className={classes.checkBoxText}>Allow Excess Hours</Typography>
            </Stack>
          </Grid>}
          {!isStakeholder() && <Grid item xs={12} sm={4}>
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={projectDetails?.allowManualTask}
                {...label}
                disabled={!isEditPermission || disabled === 'allowManualTask'}
                onChange={handleCheckChange('allowManualTask')}
              />
              <Typography className={classes.checkBoxText}>Allow Manual Task Entry</Typography>
            </Stack>
          </Grid>}
          {!isStakeholder() && <Grid item xs={12} sm={4}>
            <Stack direction="row" alignItems="center">
              <Checkbox
                checked={!!projectDetails?.hideCompletedTasks}
                {...label}
                disabled={!isEditPermission || disabled === 'hideCompletedTasks'}
                onChange={handleCheckChange('hideCompletedTasks')}
              />
              <Typography className={classes.checkBoxText}>Hide Completed Tasks</Typography>
            </Stack>
          </Grid>}
        </Grid>
      </AccordionDetails>
    </Accordion>
  );
};

export default MoreDetails;
